import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowRight, FaCalendarAlt, FaClock, FaRegPlayCircle } from 'react-icons/fa';
import { HeaderContext } from '../context/HeaderContext';

const HomeWebinar = () => {
  const { homepages, imageUrl } = useContext(HeaderContext);

  return (
    <motion.section 
      className="relative py-20 bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-800 text-white overflow-hidden"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      {/* Animated background elements */}
      <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
        <motion.div 
          className="absolute top-1/4 -right-20 w-96 h-96 bg-purple-300 rounded-full mix-blend-screen filter blur-3xl"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.1, 0.15, 0.1]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        ></motion.div>
        <motion.div 
          className="absolute bottom-1/3 -left-20 w-96 h-96 bg-indigo-300 rounded-full mix-blend-screen filter blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.1, 0.2, 0.1]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
        ></motion.div>
      </div>
      
      {/* Floating particles */}
      {[...Array(15)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full bg-white opacity-10"
          style={{
            width: Math.random() * 10 + 5 + 'px',
            height: Math.random() * 10 + 5 + 'px',
            top: Math.random() * 100 + '%',
            left: Math.random() * 100 + '%',
          }}
          animate={{
            y: [0, (Math.random() - 0.5) * 50],
            x: [0, (Math.random() - 0.5) * 30],
            opacity: [0.1, 0.2, 0.1]
          }}
          transition={{
            duration: Math.random() * 10 + 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      ))}
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, type: "spring" }}
            className="inline-flex items-center px-4 py-2 bg-white bg-opacity-20 backdrop-blur-sm rounded-full mb-6 shadow-md"
          >
            <FaCalendarAlt className="mr-2 text-yellow-300" />
            <span className="font-medium tracking-wider text-gray-900">{homepages.topBar}</span>
          </motion.div>
          
          <motion.h2
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, type: "spring", stiffness: 100 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
          >
            {homepages.headline}
          </motion.h2>
          
          <motion.p
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6, type: "spring", stiffness: 100 }}
            className="text-xl md:text-2xl mb-10 max-w-3xl mx-auto text-indigo-100 leading-relaxed"
          >
            {homepages.subHeadline}
          </motion.p>
          
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="flex flex-col sm:flex-row justify-center gap-6 mb-16"
          >
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center justify-center bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold py-4 px-8 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl"
            >
             <Link to={homepages.slug} className='no-underline flex justify-center items-center text-white'>Register Now <FaArrowRight className="ml-3 animate-pulse" /></Link> 
            </motion.button>
            {/* <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center justify-center bg-transparent hover:bg-white hover:bg-opacity-10 border-2 border-white text-white font-bold py-4 px-8 rounded-full transition-all duration-300 group"
            >
              <FaRegPlayCircle className="mr-3 text-yellow-300 group-hover:animate-pulse" /> Watch Preview
            </motion.button> */}
          </motion.div>
          
          {/* Stats or additional info */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="flex flex-wrap justify-center gap-8 mt-12"
          >
            <div className="bg-white bg-opacity-10 backdrop-blur-sm px-6 py-4 rounded-xl">
              <div className="text-2xl font-bold text-yellow-300">1,200+</div>
              <div className="text-sm uppercase tracking-wider">Participants</div>
            </div>
            <div className="bg-white bg-opacity-10 backdrop-blur-sm px-6 py-4 rounded-xl">
              <div className="text-2xl font-bold text-yellow-300">3</div>
              <div className="text-sm uppercase tracking-wider">Secrets</div>
            </div>
            <div className="bg-white bg-opacity-10 backdrop-blur-sm px-6 py-4 rounded-xl">
              <div className="text-2xl font-bold text-yellow-300">90 Min</div>
              <div className="text-sm uppercase tracking-wider">Duration</div>
            </div>
          </motion.div>
        </div>
      </div>
      
      {/* Floating webinar image */}
      <motion.div 
        className="absolute -bottom-20 right-10 w-64 h-64 hidden lg:block"
        initial={{ y: 100, opacity: 0, rotate: -5 }}
        whileInView={{ y: 0, opacity: 1, rotate: 0 }}
        transition={{ delay: 1.2, type: "spring" }}
      >
        <div className="relative w-full h-full">
          <div className="absolute inset-0 bg-purple-400 rounded-2xl transform rotate-6"></div>
          <div className="absolute inset-0 bg-white rounded-2xl shadow-2xl overflow-hidden">
            <img 
              src={imageUrl || "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80"} 
              alt="Webinar" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
              <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center">
                <FaRegPlayCircle className="text-white text-2xl" />
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.section>
  );
};

export default HomeWebinar;